import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useRegion } from '../context/RegionContext';
import FeatureNav from '../components/FeatureNav';
import TestPatientPanel from '../components/TestPatientPanel';
import ClinicalCalculatorPanel from '../components/ClinicalCalculatorPanel';
import { FeatureTutorial, TutorialButton, useTutorial } from '../components/FeatureTutorial';
import { 
  Search, Plus, Trash2, FileText, Upload, AlertTriangle,
  CheckCircle, ChevronDown, ChevronUp, Activity, User, Clipboard,
  TrendingUp, TrendingDown, Minus, Brain, FlaskConical, Heart,
  Stethoscope, Gauge, Info, Sparkles, Target, Zap, Shield, Pill, Calculator,
  Save, Share2, FolderOpen, Network, LayoutList, Layers, BookOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import DiseaseDetailPanel from '../components/DiseaseDetailPanel';
import DiseaseComparisonDashboard from '../components/DiseaseComparisonDashboard';
import DifferentialDashboard from '../components/DifferentialDashboard';
import ParameterDrillDownModal from '../components/ParameterDrillDownModal';
import PDFExportButton from '../components/PDFExportButton';
import NeuralNetworkVisualization from '../components/NeuralNetworkVisualization';
import EnhancedBodyMap from '../components/EnhancedBodyMap';
import GlobalPatientPanel from '../components/GlobalPatientPanel';
import DiseaseBrowserPanel from '../components/DiseaseBrowserPanel';
import { usePatient } from '../context/PatientContext';
import { 
  getDifferentialDiagnosis, 
  parseClinicalText, 
  analyzePatientCase,
  getAllParameters,
  NORMAL_RANGES,
} from '../data/bidirectionalMapping';
import { ORGAN_MODELS } from '../data/organModels';
import { CLINICAL_CALCULATORS, searchCalculators, getCalculatorsForDisease, getCalculatorById } from '../data/clinicalCalculators';
import { getNormalRange, checkAbnormality } from '../data/regionalUnits';

// Build comprehensive normal ranges from organ models
const buildAllNormalRanges = () => {
  const ranges = { ...NORMAL_RANGES };
  Object.values(ORGAN_MODELS).forEach(organ => {
    const categories = ['geometry', 'physiology', 'structure', 'pressures', 'cellular'];
    categories.forEach(cat => {
      const categoryData = organ[cat];
      if (!categoryData?.parameters) return;
      categoryData.parameters.forEach(param => {
        if (param.range && Array.isArray(param.range) && param.range.length === 2) {
          const [min, max] = param.range;
          ranges[param.id] = {
            min, max,
            unit: param.unit || '',
            criticalLow: Math.max(0, min - (min * 0.5)),
            criticalHigh: max + (max * 0.5),
          };
        }
      });
    });
  });
  return ranges;
};

const EXTENDED_NORMAL_RANGES = buildAllNormalRanges();

const DifferentialPage = () => {
  const { isDark } = useTheme();
  const { region, isSI } = useRegion();
  const tutorial = useTutorial('differential');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Global Patient Context
  const { activePatient, modifiers, applyModifiers } = usePatient();
  
  // State management
  const initialTab = searchParams.get('tab') || 'parameters';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedOrgan, setSelectedOrgan] = useState('');
  const [selectedParameter, setSelectedParameter] = useState('');
  const [parameterValue, setParameterValue] = useState(50);
  const [sliderValue, setSliderValue] = useState([50]);
  const [abnormalFindings, setAbnormalFindings] = useState([]);
  const [clinicalText, setClinicalText] = useState('');
  const [differentialResults, setDifferentialResults] = useState([]);
  const [expandedDisease, setExpandedDisease] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [usePhiCor, setUsePhiCor] = useState(true); // Toggle for PhiCor Analysis
  const [phicorResults, setPhicorResults] = useState(null); // Store full PhiCor analysis results
  const [analysisStartTime, setAnalysisStartTime] = useState(null); // Track analysis start time
  const [analysisTimeTaken, setAnalysisTimeTaken] = useState(null); // Store time taken for analysis
  const [elapsedTime, setElapsedTime] = useState(0); // Live elapsed time during analysis
  const [highlightedText, setHighlightedText] = useState(''); // Text currently being "analyzed"
  const [analysisStep, setAnalysisStep] = useState(''); // Current analysis step description
  const [isSaving, setIsSaving] = useState(false); // Track saving state
  
  // NEW: Disease Comparison Dashboard state
  const [selectedDiseaseForComparison, setSelectedDiseaseForComparison] = useState(null);
  const [showComparisonDashboard, setShowComparisonDashboard] = useState(false);
  
  // NEW: Clinical Synthesis state
  const [clinicalSynthesis, setClinicalSynthesis] = useState(null);
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [synthesisExpanded, setSynthesisExpanded] = useState(true);
  
  // Neural Network Visualization state
  const [showNeuralNetwork, setShowNeuralNetwork] = useState(false);
  
  // Body Map Visualization state
  const [showBodyMap, setShowBodyMap] = useState(false);
  
  // Disease Browser Panel state
  const [showDiseaseBrowser, setShowDiseaseBrowser] = useState(false);
  
  // NEW: Parameter Drill-Down Modal state
  const [drillDownModal, setDrillDownModal] = useState({
    isOpen: false,
    parameterName: null,
    parameterType: null,
    expectedValue: null,
    currentDirection: null
  });
  
  // Timer effect for live elapsed time and text highlighting simulation during analysis
  useEffect(() => {
    let timer;
    let highlightTimer;
    
    if (isAnalyzing && analysisStartTime) {
      // Update elapsed time
      timer = setInterval(() => {
        const elapsed = ((Date.now() - analysisStartTime) / 1000);
        setElapsedTime(elapsed.toFixed(1));
        
        // Simulate analysis steps based on time
        if (elapsed < 5) {
          setAnalysisStep('Parsing clinical text...');
        } else if (elapsed < 15) {
          setAnalysisStep('Extracting vital signs & lab values...');
        } else if (elapsed < 30) {
          setAnalysisStep('Matching against disease database...');
        } else if (elapsed < 45) {
          setAnalysisStep('Computing differential probabilities...');
        } else {
          setAnalysisStep('Generating clinical synthesis...');
        }
      }, 100);
      
      // Simulate text highlighting - pick random sections of the clinical text
      if (clinicalText) {
        const words = clinicalText.split(/\s+/);
        let wordIndex = 0;
        
        highlightTimer = setInterval(() => {
          if (wordIndex < words.length) {
            // Highlight 3-5 words at a time
            const chunkSize = Math.min(3 + Math.floor(Math.random() * 3), words.length - wordIndex);
            const chunk = words.slice(wordIndex, wordIndex + chunkSize).join(' ');
            setHighlightedText(chunk);
            wordIndex += chunkSize;
          } else {
            wordIndex = 0; // Loop back
          }
        }, 800);
      }
    } else {
      setHighlightedText('');
      setAnalysisStep('');
    }
    
    return () => {
      clearInterval(timer);
      clearInterval(highlightTimer);
    };
  }, [isAnalyzing, analysisStartTime, clinicalText]);
  
  // Get all parameters organized by organ
  const allParameters = useMemo(() => getAllParameters(), []);
  
  // Get organs list
  const organs = useMemo(() => {
    return Object.entries(ORGAN_MODELS).map(([id, data]) => ({
      id,
      name: data.name || id.charAt(0).toUpperCase() + id.slice(1),
      icon: data.icon || '🫀',
    }));
  }, []);
  
  // Get parameters for selected organ
  const organParameters = useMemo(() => {
    if (!selectedOrgan) return [];
    const organ = ORGAN_MODELS[selectedOrgan];
    if (!organ) return [];
    
    const params = [];
    const categories = ['geometry', 'physiology', 'structure', 'pressures', 'cellular'];
    categories.forEach(cat => {
      const categoryData = organ[cat];
      if (categoryData?.parameters) {
        categoryData.parameters.forEach(param => {
          if (param.range && Array.isArray(param.range) && param.range.length === 2) {
            params.push({
              ...param,
              category: cat,
              organId: selectedOrgan,
            });
          }
        });
      }
    });
    return params;
  }, [selectedOrgan]);
  
  // Get normal range for selected parameter
  const currentNormalRange = useMemo(() => {
    if (!selectedParameter) return null;
    const param = organParameters.find(p => p.id === selectedParameter);
    if (!param) return EXTENDED_NORMAL_RANGES[selectedParameter] || null;
    
    const [min, max] = param.range;
    return {
      min, max,
      unit: param.unit || '',
      criticalLow: Math.max(0, min - (min * 0.5)),
      criticalHigh: max + (max * 0.5),
      name: param.name,
    };
  }, [selectedParameter, organParameters]);
  
  // Update slider when parameter changes
  useEffect(() => {
    if (currentNormalRange) {
      const midpoint = (currentNormalRange.min + currentNormalRange.max) / 2;
      setParameterValue(midpoint);
      setSliderValue([midpoint]);
    }
  }, [selectedParameter, currentNormalRange]);
  
  // Determine value status
  const getValueStatus = useCallback((value, range) => {
    if (!range) return 'unknown';
    if (value < range.criticalLow) return 'critical-low';
    if (value < range.min) return 'low';
    if (value > range.criticalHigh) return 'critical-high';
    if (value > range.max) return 'high';
    return 'normal';
  }, []);
  
  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'critical-low': return 'bg-red-500';
      case 'low': return 'bg-blue-500';
      case 'normal': return 'bg-green-500';
      case 'high': return 'bg-orange-500';
      case 'critical-high': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };
  
  // Build comprehensive Neural Network visualization data with syndromes, organs, and many-to-many connections
  const neuralNetworkData = useMemo(() => {
    if (differentialResults.length === 0 && abnormalFindings.length === 0) {
      return { nodes: [], connections: [] };
    }

    const nodes = [];
    const connections = [];
    const addedNodes = new Set();
    const addedConnections = new Set();
    
    // Helper to add unique node
    const addNode = (node) => {
      if (!addedNodes.has(node.id)) {
        nodes.push(node);
        addedNodes.add(node.id);
      }
    };
    
    // Helper to add unique connection
    const addConnection = (conn) => {
      const connKey = `${conn.source}-${conn.target}`;
      const reverseKey = `${conn.target}-${conn.source}`;
      if (!addedConnections.has(connKey) && !addedConnections.has(reverseKey)) {
        connections.push(conn);
        addedConnections.add(connKey);
      }
    };
    
    // 1. PATIENT NODE - Central hub
    addNode({
      id: 'patient',
      label: 'Patient',
      type: 'patient',
      category: 'Central Hub',
      value: 100
    });
    
    // 2. SYMPTOM & LAB NODES - First layer
    const symptomNodes = [];
    const labNodes = [];
    
    abnormalFindings.forEach((finding, idx) => {
      const isLab = finding.type === 'lab' || finding.unit;
      const nodeId = `finding-${idx}`;
      const findingValue = Math.abs(finding.percentFromNormal || 50);
      
      const node = {
        id: nodeId,
        label: finding.name || finding.parameter,
        type: isLab ? 'lab' : 'symptom',
        category: isLab ? 'Lab Value' : 'Symptom',
        value: Math.min(findingValue, 100),
        severity: finding.severity || 'moderate',
        direction: finding.direction
      };
      
      addNode(node);
      if (isLab) labNodes.push(node);
      else symptomNodes.push(node);
      
      // Connect to patient with severity-based strength
      const severityStrength = finding.severity === 'critical' ? 95 : 
                              finding.severity === 'severe' ? 85 : 
                              finding.severity === 'moderate' ? 70 : 55;
      addConnection({
        source: 'patient',
        target: nodeId,
        strength: severityStrength,
        label: 'presents',
        type: 'direct'
      });
    });
    
    // 3. EXTRACT SYNDROMES from synthesis data (clinical patterns)
    const syndromes = new Map();
    
    // Parse synthesis for clinical syndromes/patterns
    if (clinicalSynthesis?.key_pattern || phicorResults?.dominant_syndromes?.[0]) {
      const syndromeId = 'syndrome-primary';
      const patternLabel = clinicalSynthesis?.key_pattern || 
                          phicorResults?.dominant_syndromes?.[0]?.name ||
                          'Primary Clinical Pattern';
      syndromes.set(syndromeId, {
        id: syndromeId,
        label: patternLabel.substring(0, 35),
        type: 'syndrome',
        category: 'Clinical Pattern',
        value: 85
      });
    }
    
    // Extract syndromes from differential reasoning
    differentialResults.slice(0, 6).forEach((diff, idx) => {
      const reasoning = diff.reasoning || diff.clinical_reasoning || '';
      
      // Look for pattern keywords to create syndrome nodes
      const patterns = [
        { keyword: 'acute', syndrome: 'Acute Presentation' },
        { keyword: 'chronic', syndrome: 'Chronic Course' },
        { keyword: 'inflammatory', syndrome: 'Inflammatory Process' },
        { keyword: 'ischemic', syndrome: 'Ischemic Pattern' },
        { keyword: 'infectious', syndrome: 'Infectious Pattern' },
        { keyword: 'metabolic', syndrome: 'Metabolic Derangement' },
        { keyword: 'cardiac', syndrome: 'Cardiac Involvement' },
        { keyword: 'respiratory', syndrome: 'Respiratory Pattern' },
        { keyword: 'neurological', syndrome: 'Neurological Pattern' },
        { keyword: 'gastrointestinal', syndrome: 'GI Pattern' },
      ];
      
      patterns.forEach(({ keyword, syndrome }) => {
        if (reasoning.toLowerCase().includes(keyword) || 
            diff.diseaseName?.toLowerCase().includes(keyword) ||
            diff.name?.toLowerCase().includes(keyword)) {
          const syndromeId = `syndrome-${keyword}`;
          if (!syndromes.has(syndromeId)) {
            syndromes.set(syndromeId, {
              id: syndromeId,
              label: syndrome,
              type: 'syndrome',
              category: 'Clinical Pattern',
              value: 60 + Math.random() * 20
            });
          }
        }
      });
    });
    
    // Add syndrome nodes
    syndromes.forEach(syndrome => addNode(syndrome));
    
    // 4. ORGAN SYSTEM NODES - Affected body systems
    const organSystems = new Map();
    const organKeywords = {
      heart: { name: 'Heart', system: 'Cardiovascular' },
      cardiac: { name: 'Heart', system: 'Cardiovascular' },
      coronary: { name: 'Coronary Arteries', system: 'Cardiovascular' },
      lung: { name: 'Lungs', system: 'Respiratory' },
      pulmonary: { name: 'Lungs', system: 'Respiratory' },
      respiratory: { name: 'Respiratory System', system: 'Respiratory' },
      liver: { name: 'Liver', system: 'Hepatic' },
      hepatic: { name: 'Liver', system: 'Hepatic' },
      kidney: { name: 'Kidneys', system: 'Renal' },
      renal: { name: 'Kidneys', system: 'Renal' },
      brain: { name: 'Brain', system: 'Neurological' },
      neuro: { name: 'Nervous System', system: 'Neurological' },
      stomach: { name: 'Stomach', system: 'GI' },
      intestin: { name: 'Intestines', system: 'GI' },
      pancrea: { name: 'Pancreas', system: 'GI' },
      blood: { name: 'Blood', system: 'Hematologic' },
      bone: { name: 'Bone Marrow', system: 'Hematologic' },
      thyroid: { name: 'Thyroid', system: 'Endocrine' },
      adrenal: { name: 'Adrenal Glands', system: 'Endocrine' },
    };
    
    // Scan all text for organ mentions
    const allText = [
      ...abnormalFindings.map(f => f.name || f.parameter),
      ...differentialResults.map(d => `${d.diseaseName || d.name} ${d.reasoning || ''}`),
      clinicalSynthesis?.pathophysiological_connection || '',
      phicorResults?.patient_summary || ''
    ].join(' ').toLowerCase();
    
    Object.entries(organKeywords).forEach(([keyword, { name, system }]) => {
      if (allText.includes(keyword)) {
        const organId = `organ-${system.toLowerCase().replace(/\s/g, '-')}`;
        if (!organSystems.has(organId)) {
          organSystems.set(organId, {
            id: organId,
            label: name,
            type: 'organ',
            category: system,
            value: 70
          });
        }
      }
    });
    
    // Add organ nodes
    organSystems.forEach(organ => addNode(organ));
    
    // 5. DIAGNOSIS NODES - Final outcomes
    differentialResults.slice(0, 8).forEach((diff, idx) => {
      const nodeId = `diagnosis-${idx}`;
      const probability = diff.probabilityScore || diff.probability || diff.matchScore || 0;
      
      addNode({
        id: nodeId,
        label: diff.diseaseName || diff.name,
        type: 'diagnosis',
        category: `#${idx + 1} Diagnosis`,
        value: probability,
        icd10: diff.icd10,
        confidence: diff.confidence
      });
    });
    
    // 6. BUILD CONNECTIONS - Many-to-many relationships
    
    // 6a. Symptoms/Labs → Syndromes (many-to-many)
    abnormalFindings.forEach((finding, fIdx) => {
      const findingId = `finding-${fIdx}`;
      const findingText = (finding.name || finding.parameter || '').toLowerCase();
      
      syndromes.forEach((syndrome, syndromeId) => {
        const syndromeText = syndrome.label.toLowerCase();
        // Check for semantic relationship
        const relatedKeywords = {
          'acute': ['sudden', 'severe', 'rapid', 'emergency'],
          'inflammatory': ['elevated', 'high', 'crp', 'esr', 'wbc', 'fever', 'pain'],
          'ischemic': ['troponin', 'chest', 'ecg', 'st', 'heart'],
          'infectious': ['fever', 'wbc', 'culture', 'temperature'],
          'cardiac': ['chest', 'heart', 'troponin', 'bnp', 'ecg', 'murmur'],
          'respiratory': ['breath', 'oxygen', 'saturation', 'lung', 'cough', 'dyspnea'],
        };
        
        let matchStrength = 0;
        Object.entries(relatedKeywords).forEach(([key, words]) => {
          if (syndromeText.includes(key)) {
            words.forEach(word => {
              if (findingText.includes(word)) matchStrength += 15;
            });
          }
        });
        
        if (matchStrength > 0) {
          addConnection({
            source: findingId,
            target: syndromeId,
            strength: Math.min(matchStrength + 30, 85),
            label: 'indicates',
            type: 'indirect'
          });
        }
      });
    });
    
    // 6b. Symptoms/Labs → Organs (direct impact)
    abnormalFindings.forEach((finding, fIdx) => {
      const findingId = `finding-${fIdx}`;
      const findingText = (finding.name || finding.parameter || '').toLowerCase();
      
      organSystems.forEach((organ, organId) => {
        const organText = organ.label.toLowerCase();
        const systemText = organ.category.toLowerCase();
        
        // Check for organ-finding relationship
        if (findingText.includes(organText) || 
            organText.includes(findingText.split(' ')[0]) ||
            (systemText === 'cardiovascular' && /troponin|bnp|chest|heart|ecg/i.test(findingText)) ||
            (systemText === 'respiratory' && /oxygen|breath|lung|cough|saturation/i.test(findingText)) ||
            (systemText === 'renal' && /creatinine|bun|gfr|kidney/i.test(findingText)) ||
            (systemText === 'hepatic' && /alt|ast|bilirubin|liver|albumin/i.test(findingText))) {
          addConnection({
            source: findingId,
            target: organId,
            strength: 65,
            label: 'affects',
            type: 'causal'
          });
        }
      });
    });
    
    // 6c. Syndromes → Diagnoses (pattern matching)
    syndromes.forEach((syndrome, syndromeId) => {
      const syndromeText = syndrome.label.toLowerCase();
      
      differentialResults.slice(0, 8).forEach((diff, dIdx) => {
        const diagId = `diagnosis-${dIdx}`;
        const diagText = `${diff.diseaseName || diff.name} ${diff.reasoning || ''}`.toLowerCase();
        
        // Check for syndrome-diagnosis relationship
        const syndromeKeyword = syndromeText.split(' ')[0];
        if (diagText.includes(syndromeKeyword)) {
          const probability = diff.probabilityScore || diff.probability || diff.matchScore || 50;
          addConnection({
            source: syndromeId,
            target: diagId,
            strength: Math.round(probability * 0.8),
            label: 'suggests',
            type: 'indirect'
          });
        }
      });
    });
    
    // 6d. Organs → Diagnoses (anatomical relationship)
    organSystems.forEach((organ, organId) => {
      const organText = organ.label.toLowerCase();
      const systemText = organ.category.toLowerCase();
      
      differentialResults.slice(0, 8).forEach((diff, dIdx) => {
        const diagId = `diagnosis-${dIdx}`;
        const diagText = `${diff.diseaseName || diff.name}`.toLowerCase();
        
        if (diagText.includes(organText) ||
            (systemText === 'cardiovascular' && /infarct|angina|heart|cardiac|coronary/i.test(diagText)) ||
            (systemText === 'respiratory' && /pneumonia|emboli|copd|asthma|lung/i.test(diagText)) ||
            (systemText === 'hepatic' && /hepat|cirrhos|liver/i.test(diagText)) ||
            (systemText === 'renal' && /kidney|renal|nephr/i.test(diagText))) {
          const probability = diff.probabilityScore || diff.probability || diff.matchScore || 50;
          addConnection({
            source: organId,
            target: diagId,
            strength: Math.round(probability * 0.7),
            label: 'involves',
            type: 'causal'
          });
        }
      });
    });
    
    // 6e. Direct Findings → Diagnoses (supporting evidence)
    differentialResults.slice(0, 8).forEach((diff, dIdx) => {
      const diagId = `diagnosis-${dIdx}`;
      const probability = diff.probabilityScore || diff.probability || diff.matchScore || 0;
      
      // Get supporting findings
      const supportingFindings = diff.matchingParameters || diff.supporting_findings || 
                                diff.key_supporting_findings || [];
      
      supportingFindings.forEach((param, pIdx) => {
        const paramName = typeof param === 'string' ? param : 
                         param.name || param.parameter || param.finding || '';
        
        // Find matching finding node
        abnormalFindings.forEach((finding, fIdx) => {
          const findingName = (finding.name || finding.parameter || '').toLowerCase();
          if (findingName.includes(paramName.toLowerCase()) ||
              paramName.toLowerCase().includes(findingName)) {
            const findingId = `finding-${fIdx}`;
            const strength = Math.max(30, probability - (pIdx * 5));
            addConnection({
              source: findingId,
              target: diagId,
              strength: Math.min(strength, 90),
              label: `+${Math.round(strength * 0.3)}%`,
              type: 'direct'
            });
          }
        });
      });
      
      // Also connect patient directly to high-probability diagnoses
      if (probability >= 50) {
        addConnection({
          source: 'patient',
          target: diagId,
          strength: probability,
          label: `${probability}%`,
          type: 'causal'
        });
      }
    });
    
    // 6f. Cross-connections between diagnoses (differential relationships)
    differentialResults.slice(0, 5).forEach((diff1, idx1) => {
      differentialResults.slice(idx1 + 1, 6).forEach((diff2, idx2) => {
        const diagId1 = `diagnosis-${idx1}`;
        const diagId2 = `diagnosis-${idx1 + 1 + idx2}`;
        
        // Check for related diagnoses (shared organ system, similar names)
        const name1 = (diff1.diseaseName || diff1.name || '').toLowerCase();
        const name2 = (diff2.diseaseName || diff2.name || '').toLowerCase();
        
        // Check for shared keywords
        const keywords1 = new Set(name1.split(/\s+/));
        const keywords2 = new Set(name2.split(/\s+/));
        const shared = [...keywords1].filter(k => keywords2.has(k) && k.length > 3);
        
        if (shared.length > 0) {
          const prob1 = diff1.probabilityScore || diff1.probability || 50;
          const prob2 = diff2.probabilityScore || diff2.probability || 50;
          addConnection({
            source: diagId1,
            target: diagId2,
            strength: Math.round((prob1 + prob2) / 4),
            label: 'r=' + ((prob1 + prob2) / 200).toFixed(2),
            type: 'correlation',
            isCorrelation: true
          });
        }
      });
    });
    
    return { nodes, connections };
  }, [differentialResults, abnormalFindings, clinicalSynthesis, phicorResults]);
  
  // Build Body Map data from differential diagnosis results
  const bodyMapData = useMemo(() => {
    const effects = [];
    const connections = [];
    
    // Map findings to organ effects
    abnormalFindings.forEach(finding => {
      const organName = finding.organ || 'general';
      const effect = {
        organ: organName,
        parameter: finding.parameterName || finding.name,
        change: finding.direction === 'increase' ? 'elevated' : 'reduced',
        value: finding.value,
        unit: finding.unit || '',
        severity: finding.severity || 'moderate'
      };
      effects.push(effect);
    });
    
    // Add organ effects from top diagnoses
    differentialResults.slice(0, 5).forEach(diagnosis => {
      const diagName = diagnosis.diseaseName || diagnosis.name;
      const probability = diagnosis.probabilityScore || diagnosis.probability || 0;
      
      // Extract affected organs from diagnosis name and reasoning
      const diagText = `${diagName} ${diagnosis.reasoning || ''}`.toLowerCase();
      
      const organMappings = [
        { keywords: ['heart', 'cardiac', 'coronary', 'myocardial', 'infarct'], organ: 'heart' },
        { keywords: ['lung', 'pulmonary', 'respiratory', 'pneumo'], organ: 'lungs' },
        { keywords: ['liver', 'hepatic', 'hepato'], organ: 'liver' },
        { keywords: ['kidney', 'renal', 'nephro'], organ: 'kidneys' },
        { keywords: ['brain', 'cerebral', 'neuro', 'stroke'], organ: 'brain' },
        { keywords: ['stomach', 'gastric', 'gi', 'intestin', 'bowel'], organ: 'stomach' },
        { keywords: ['pancrea'], organ: 'pancreas' },
        { keywords: ['blood', 'anemia', 'hematol'], organ: 'blood' },
        { keywords: ['thyroid'], organ: 'thyroid' },
      ];
      
      organMappings.forEach(({ keywords, organ }) => {
        if (keywords.some(kw => diagText.includes(kw))) {
          connections.push({
            source: diagName,
            organ: organ,
            strength: probability,
            effect: probability > 60 ? 'primary' : 'secondary'
          });
        }
      });
    });
    
    return { effects, connections };
  }, [differentialResults, abnormalFindings]);
  
  // Add abnormal finding
  const addFinding = useCallback(() => {
    if (!selectedParameter || !currentNormalRange) return;
    
    const status = getValueStatus(parameterValue, currentNormalRange);
    if (status === 'normal') {
      alert('Value is within normal range. Adjust to add an abnormal finding.');
      return;
    }
    
    const direction = parameterValue > currentNormalRange.max ? 'increase' : 'decrease';
    const deviation = direction === 'increase' 
      ? ((parameterValue - currentNormalRange.max) / currentNormalRange.max * 100).toFixed(0)
      : ((currentNormalRange.min - parameterValue) / currentNormalRange.min * 100).toFixed(0);
    
    // Create full parameter ID with organ prefix for disease mapping
    const fullParameterId = `${selectedOrgan}.${selectedParameter}`;
    
    const newFinding = {
      id: `${selectedOrgan}-${selectedParameter}-${Date.now()}`,
      organ: selectedOrgan,
      parameterId: fullParameterId,  // Use full parameter ID for disease mapping
      parameterName: currentNormalRange.name || selectedParameter,
      value: parameterValue,
      unit: currentNormalRange.unit,
      direction,
      severity: status.includes('critical') ? 'critical' : 'moderate',
      deviation: `${deviation}%`,
      normalRange: `${currentNormalRange.min}-${currentNormalRange.max}`,
    };
    
    setAbnormalFindings(prev => [...prev, newFinding]);
    setSelectedParameter('');
  }, [selectedParameter, parameterValue, currentNormalRange, selectedOrgan, getValueStatus]);
  
  // Remove finding
  const removeFinding = (id) => {
    setAbnormalFindings(prev => prev.filter(f => f.id !== id));
  };
  
  // Run differential diagnosis
  const runDiagnosis = useCallback((overrideFindings = null) => {
    // Skip local diagnosis if PhiCor already provided differentials
    if (phicorResults?.differentials?.length > 0) {
      console.log('runDiagnosis: Skipping - PhiCor differentials already present');
      return;
    }
    
    setIsAnalyzing(true);
    console.log('runDiagnosis: Running LOCAL diagnosis (not PhiCor)');
    
    setTimeout(() => {
      let results = [];
      const findings = overrideFindings || abnormalFindings;
      
      console.log('runDiagnosis: findings count:', findings.length);
      
      if (findings.length > 0) {
        const findingsForAnalysis = findings.map(f => ({
          parameterId: f.parameterId,
          direction: f.direction,
          severity: f.severity,
          value: f.value,
          diseases: f.diseases || [],
        }));
        console.log('runDiagnosis: findingsForAnalysis:', findingsForAnalysis);
        results = getDifferentialDiagnosis(findingsForAnalysis);
        console.log('runDiagnosis: getDifferentialDiagnosis returned:', results.length, 'results');
        
        // Also add disease suggestions from imaging findings
        const imagingDiseases = new Set();
        findings.forEach(f => {
          if (f.diseases && f.diseases.length > 0) {
            f.diseases.forEach(d => imagingDiseases.add(d));
          }
        });
        
        // Boost scores for diseases found in imaging findings
        results = results.map(r => {
          const icdBase = r.icd10?.split('_')[0] || r.icd10?.split('.')[0];
          if (imagingDiseases.has(r.icd10) || imagingDiseases.has(icdBase)) {
            return { ...r, probabilityScore: Math.min(100, Math.round(((r.probabilityScore || 0) + 20) * 10) / 10) };
          }
          return r;
        });
      }
      
      if (clinicalText.trim()) {
        const textAnalysis = analyzePatientCase(clinicalText);
        if (textAnalysis.differential?.length > 0) {
          // Merge with existing results
          const existingCodes = new Set(results.map(r => r.icd10));
          textAnalysis.differential.forEach(d => {
            if (!existingCodes.has(d.icd10)) {
              results.push(d);
            }
          });
        }
      }
      
      // Sort by probability
      results.sort((a, b) => (b.probabilityScore || 0) - (a.probabilityScore || 0));
      
      setDifferentialResults(results.slice(0, 20));
      setIsAnalyzing(false);
    }, 500);
  }, [abnormalFindings, clinicalText, phicorResults]);
  
  // Track if PhiCor analysis is active to prevent race conditions
  const [phicorActive, setPhicorActive] = useState(false);
  
  // Auto-run diagnosis when findings change - but only if PhiCor hasn't provided results
  useEffect(() => {
    // Skip if we have PhiCor differentials already
    if (phicorResults?.differentials?.length > 0) {
      console.log('Skipping local diagnosis - PhiCor differentials already present:', phicorResults.differentials.length);
      return;
    }
    
    // Skip if PhiCor analysis is currently running or was just completed
    if (phicorActive) {
      console.log('Skipping local diagnosis - PhiCor is active');
      return;
    }
    
    // Skip if usePhiCor is enabled and we're analyzing - wait for PhiCor results
    if (usePhiCor && isAnalyzing) {
      console.log('Skipping local diagnosis - waiting for PhiCor analysis');
      return;
    }
    
    // Skip if we already have differential results from PhiCor (check the results array directly)
    if (usePhiCor && differentialResults.length > 0 && differentialResults[0]?.id?.startsWith('phicor-diff-')) {
      console.log('Skipping local diagnosis - PhiCor results already displayed');
      return;
    }
    
    // Only run local diagnosis if PhiCor is disabled
    if (!usePhiCor && (abnormalFindings.length > 0 || clinicalText.trim())) {
      console.log('Running local diagnosis - PhiCor disabled');
      runDiagnosis();
    } else if (!usePhiCor && abnormalFindings.length === 0 && !clinicalText.trim()) {
      setDifferentialResults([]);
    }
  }, [abnormalFindings, runDiagnosis, phicorResults, phicorActive, usePhiCor, isAnalyzing, clinicalText, differentialResults]);
  
  // Analyze clinical text - with PhiCor Analysis
  const handleAnalyzeText = async () => {
    if (!clinicalText.trim()) return;
    
    const startTime = Date.now();
    setAnalysisStartTime(startTime);
    setElapsedTime(0);
    setAnalysisTimeTaken(null);
    setIsAnalyzing(true);
    setPhicorResults(null);
    if (usePhiCor) {
      setPhicorActive(true); // Mark PhiCor as active to prevent useEffect from running local diagnosis
    }
    
    try {
      let findings = [];
      let symptoms = [];
      let result = null; // Declare result at function scope
      
      if (usePhiCor) {
        // Use PhiCor Analysis - comprehensive physiology-first reasoning
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const unitSystem = isSI ? 'SI' : 'conventional';
        
        console.log(`Using PhiCor Analysis (Region: ${region}, Units: ${unitSystem})`);
        
        // Retry logic for API calls
        let retryCount = 0;
        const maxRetries = 2;
        
        while (retryCount <= maxRetries) {
          try {
            const response = await fetch(`${backendUrl}/api/clinical/interpret`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                text: clinicalText,
                region: region,
                source_type: 'free_text'
              })
            });
            
            if (response.ok) {
              result = await response.json();
              console.log('PhiCor Analysis result:', result);
              
              if (result.success && (result.findings?.length > 0 || result.differentials?.length > 0)) {
                // Store full PhiCor results for display
                setPhicorResults(result);
                break; // Success, exit retry loop
              } else if (retryCount < maxRetries) {
                console.warn(`PhiCor Analysis returned incomplete data, retrying... (${retryCount + 1}/${maxRetries})`);
                retryCount++;
                continue;
              }
            } else {
              if (retryCount < maxRetries) {
                retryCount++;
                continue;
              }
            }
            break;
          } catch (fetchError) {
            console.error('Fetch error:', fetchError);
            if (retryCount < maxRetries) {
              retryCount++;
              continue;
            }
            break;
          }
        }
        
        // Process successful result
        if (result?.success && result.findings?.length > 0) {
          findings = result.findings.map((f, idx) => ({
            id: `phicor-${Date.now()}-${idx}`,
            organ: f.organ_system || f.organ || 'unknown',
            parameterId: f.parameter_id,
            parameterName: f.name || f.parameter_id,
            value: f.value || '',
            unit: f.unit || '',
            direction: f.direction,
            severity: f.severity || 1,
            deviation: '',
            normalRange: '',
            source: 'phicor-analysis',
            originalText: f.original_text || '',
            type: f.type || 'lab',
            physiologicImplication: f.physiologic_implication || '',
          }));
        }
          
        // Add symptoms as findings too
          if (result.symptoms?.length > 0) {
            symptoms = result.symptoms.map((s, idx) => {
              const symptomName = typeof s === 'object' ? s.name : s;
              return {
                id: `symptom-${Date.now()}-${idx}`,
                organ: 'general',
                parameterId: symptomName.replace(/\s+/g, '_').toLowerCase(),
                parameterName: symptomName,
                value: s.severity || '',
                unit: '',
                direction: 'present',
                severity: s.severity || 'moderate',
                deviation: '',
                normalRange: '',
                source: 'phicor-analysis',
                type: 'symptom',
                clinical_significance: s.clinical_significance || '',
              };
            });
          }
          
          // If PhiCor returned differentials, use those directly instead of local calculation
          if (result.differentials?.length > 0) {
            const phicorDifferentials = result.differentials.map((d, idx) => ({
              id: `phicor-diff-${idx}`,
              diseaseId: d.icd_code || d.name.toLowerCase().replace(/\s+/g, '_'),
              diseaseName: d.name,
              probability: d.probability / 100, // Convert to 0-1 scale
              probabilityScore: d.probability, // Keep original percentage for display
              role: d.role,
              matchingParameters: d.matching_parameters || [],
              missingFeatures: d.missing_features || [],
              icdCode: d.icd_code,
              icd10: d.icd_code, // Alias for compatibility
              // New comprehensive fields
              rank: d.rank,
              clinical_reasoning: d.clinical_reasoning,
              supporting_findings: d.supporting_findings || [],
              key_discriminating_features: d.key_discriminating_features || [],
              against_diagnosis: d.against_diagnosis || [],
              anchor_criteria_met: d.anchor_criteria_met,
            }));
            
            // Set PhiCor differentials directly
            setDifferentialResults(phicorDifferentials);
          }
        
        // Fallback to regex parsing if PhiCor didn't return usable data
        if (!result?.success || (!result?.findings?.length && !result?.differentials?.length)) {
          console.warn('PhiCor Analysis failed or returned no data, falling back to regex parsing');
          const parsed = parseClinicalText(clinicalText);
          findings = parsed.findings || [];
        }
      } else {
        // Use regex-based parsing when PhiCor is disabled
        const parsed = parseClinicalText(clinicalText);
        console.log('Regex parsed clinical text:', parsed);
        findings = parsed.findings || [];
      }
      
      const allFindings = [...findings, ...symptoms];
      
      if (allFindings.length > 0) {
        const newFindings = allFindings.map((f, idx) => ({
          id: f.id || `text-${Date.now()}-${idx}`,
          organ: f.organ || 'unknown',
          parameterId: f.parameterId || f.parameter_id,
          parameterName: f.parameterName || f.name || f.parameterId,
          value: f.value || '',
          unit: f.unit || '',
          direction: f.direction,
          severity: f.severity || 'moderate',
          deviation: '',
          normalRange: f.normalRange || '',
          source: f.source || 'clinical-text',
          diseases: f.diseases || [],
          type: f.type || 'lab',
          physiologicImplication: f.physiologicImplication || '',
        }));
        
        // Check if we should run local diagnosis
        const hasPhicorDifferentials = result?.differentials?.length > 0;
        
        // REPLACE findings instead of appending to prevent duplicates
        setAbnormalFindings(newFindings);
        
        if (hasPhicorDifferentials) {
          // PhiCor provided differentials - keep them
          setTimeout(() => {
            setPhicorActive(false);
          }, 1000);
        } else {
          // Run local diagnosis if no PhiCor differentials
          setPhicorActive(false);
          setTimeout(() => {
            runDiagnosis(newFindings);
          }, 100);
        }
        
        const timeTaken = ((Date.now() - startTime) / 1000).toFixed(1);
        setAnalysisTimeTaken(timeTaken);
        setIsAnalyzing(false);
        
        // Synthesis will be auto-triggered by useEffect when abnormalFindings updates
        // The useEffect has a 1500ms debounce which ensures state is settled
      } else {
        setAnalysisTimeTaken(null);
        setIsAnalyzing(false);
        setPhicorActive(false); // Reset PhiCor active flag
        alert('No clinical findings detected. Try adding more specific clinical details.');
      }
    } catch (error) {
      console.error('Error analyzing clinical text:', error);
      setAnalysisTimeTaken(null);
      setIsAnalyzing(false);
      setPhicorActive(false); // Reset PhiCor active flag on error
      
      // Fall back to regex parsing on error
      const parsed = parseClinicalText(clinicalText);
      if (parsed.findings?.length > 0) {
        const newFindings = parsed.findings.map((f, idx) => ({
          id: `text-${Date.now()}-${idx}`,
          organ: f.organ || 'unknown',
          parameterId: f.parameterId,
          parameterName: f.name || f.parameterId,
          value: f.value || '',
          unit: f.unit || '',
          direction: f.direction,
          severity: f.severity || 'moderate',
          deviation: '',
          normalRange: f.normalRange || '',
          source: 'clinical-text',
          diseases: f.diseases || [],
        }));
        
        setAbnormalFindings(prev => {
          const updated = [...prev, ...newFindings];
          setTimeout(() => {
            runDiagnosis(updated);
          }, 100);
          return updated;
        });
      } else {
        alert('Error analyzing text. Please try again.');
      }
    }
  };
  
  // NEW: Run AI-powered clinical synthesis
  const runClinicalSynthesis = useCallback(async () => {
    // Need either findings or substantial clinical text to synthesize
    console.log('runClinicalSynthesis called:', { 
      findingsCount: abnormalFindings.length, 
      hasText: !!clinicalText.trim(),
      resultsCount: differentialResults.length
    });
    
    // Synthesis is useful if we have findings OR clinical text with differential results
    if (abnormalFindings.length === 0 && !clinicalText.trim() && differentialResults.length === 0) {
      setClinicalSynthesis(null);
      return;
    }
    
    setIsSynthesizing(true);
    console.log('Starting synthesis API call...');
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      console.log('Backend URL:', backendUrl);
      
      // Separate findings by type - treat ALL findings as lab findings if from parameters
      const labFindings = abnormalFindings.filter(f => !f.diseases?.length);
      const imagingFindings = abnormalFindings.filter(f => f.diseases?.length > 0);
      const symptoms = abnormalFindings.filter(f => f.source === 'clinical-text' && f.type === 'symptom');
      
      // Include info from differential results
      const topDifferentials = differentialResults.slice(0, 5).map(d => d.diseaseName).join(', ');
      
      // Generate clinical context from findings if no text provided
      let contextText = clinicalText;
      if (!contextText && abnormalFindings.length > 0) {
        const findingsSummary = abnormalFindings.map(f => 
          `${f.parameterName || f.parameterId}: ${f.value} ${f.unit || ''} (${f.direction}, ${f.severity})`
        ).join('; ');
        contextText = `Patient presents with the following abnormal parameters: ${findingsSummary}. Top differential diagnoses being considered: ${topDifferentials}`;
      } else if (topDifferentials && !contextText.includes('differential')) {
        contextText = `${contextText}. Top differential diagnoses being considered: ${topDifferentials}`;
      }
      
      const requestBody = {
        clinical_text: contextText || `Top differential diagnoses being considered: ${topDifferentials}`,
        lab_findings: labFindings.map(f => ({
          parameterId: f.parameterId || '',
          name: f.parameterName || f.parameterId || '',
          value: f.value || null,  // Pass the actual value (string or number)
          unit: f.unit || '',
          direction: f.direction || '',
          normalRange: f.normalRange || '',
          severity: f.severity || 'moderate'
        })),
        imaging_findings: imagingFindings.map(f => ({
          parameterId: f.parameterId || '',
          name: f.parameterName || f.parameterId || '',
          value: null,
          unit: '',
          direction: f.direction || '',
          normalRange: '',
          severity: f.severity || 'moderate'
        })),
        symptoms: symptoms.map(f => ({
          parameterId: f.parameterId || '',
          name: f.parameterName || f.parameterId || '',
          value: null,
          unit: '',
          direction: 'present',
          normalRange: '',
          severity: 'moderate'
        })),
        patient_info: null
      };
      
      console.log('Synthesis request:', requestBody);
      
      const response = await fetch(`${backendUrl}/api/diagnosis/synthesize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      console.log('Synthesis response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Synthesis response:', data);
        if (data.success && data.synthesis) {
          setClinicalSynthesis(data.synthesis);
          console.log('Clinical synthesis set!');
        }
      } else {
        try {
          const errorData = await response.json();
          console.error('Synthesis API error (JSON):', errorData);
        } catch (e) {
          const errorText = await response.text();
          console.error('Synthesis API error (text):', errorText);
        }
      }
    } catch (error) {
      console.error('Clinical synthesis error:', error);
    } finally {
      setIsSynthesizing(false);
    }
  }, [abnormalFindings, clinicalText, differentialResults]);
  
  // Auto-run synthesis when findings change (with debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('Synthesis check:', { findingsCount: abnormalFindings.length, hasText: !!clinicalText.trim(), resultsCount: differentialResults.length });
      // Trigger synthesis if we have 1+ findings with differential results OR clinical text with results
      if ((abnormalFindings.length >= 1 && differentialResults.length > 0) || (clinicalText.trim() && differentialResults.length > 0)) {
        console.log('Triggering clinical synthesis...');
        runClinicalSynthesis();
      } else {
        setClinicalSynthesis(null);
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [abnormalFindings, clinicalText, differentialResults, runClinicalSynthesis]);
  
  // Save case function
  const saveCase = useCallback(async () => {
    if (differentialResults.length === 0 && !clinicalText.trim()) {
      toast.error('No analysis to save. Run an analysis first.');
      return;
    }
    
    setIsSaving(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Get dominant syndrome from phicorResults
      const dominantSyndrome = phicorResults?.dominant_syndromes?.[0] || clinicalSynthesis?.dominant_syndrome || null;
      
      const caseData = {
        clinical_text: clinicalText,
        patient_summary: phicorResults?.patient_summary || null,
        findings: abnormalFindings.map(f => ({
          parameterId: f.parameterId,
          parameterName: f.parameterName,
          value: f.value,
          unit: f.unit,
          direction: f.direction,
          status: f.status,
          normalRange: f.normalRange
        })),
        symptoms: phicorResults?.symptoms || abnormalFindings.filter(f => f.type === 'symptom'),
        differentials: differentialResults.map(d => ({
          rank: d.rank,
          name: d.diseaseName || d.name,
          diagnosis: d.diseaseName || d.name,
          probability: d.probabilityScore || d.probability || d.matchScore || 0,
          role: d.role || 'Differential',
          clinical_reasoning: d.clinical_reasoning || d.reasoning,
          supporting_findings: d.matchingParameters || d.supporting_findings || [],
          syndrome_alignment: d.syndrome_alignment
        })),
        synthesis: clinicalSynthesis,
        dominant_syndrome: dominantSyndrome,
        safety_alerts: phicorResults?.safety_alerts || [],
        tags: []
      };
      
      // Auto-generate tags from differentials
      if (differentialResults.length > 0) {
        const topDiagnosis = differentialResults[0].diseaseName || differentialResults[0].name;
        if (topDiagnosis) {
          // Extract key terms for tags
          const terms = topDiagnosis.toLowerCase().split(/[\s,]+/).filter(t => t.length > 3);
          caseData.tags = terms.slice(0, 3);
        }
      }
      
      const response = await fetch(`${backendUrl}/api/cases/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(caseData)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(
          <div>
            <p>Case saved successfully!</p>
            <button 
              onClick={() => navigate(`/cases/${data.case_id}`)}
              className="text-sm underline mt-1"
            >
              View case →
            </button>
          </div>
        );
      } else {
        throw new Error('Failed to save case');
      }
    } catch (error) {
      console.error('Error saving case:', error);
      toast.error('Failed to save case. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [clinicalText, abnormalFindings, differentialResults, clinicalSynthesis, phicorResults, navigate]);
  
  // Get probability color
  const getProbabilityColor = (score) => {
    if (score >= 70) return 'from-green-500 to-emerald-500';
    if (score >= 50) return 'from-yellow-500 to-amber-500';
    if (score >= 30) return 'from-orange-500 to-red-500';
    return 'from-gray-500 to-gray-600';
  };
  
  return (
    <>
      <Helmet>
        <title>Differential Diagnosis | PhiCor.ai - Mathematical Clinical Reasoning</title>
        <meta name="description" content="Advanced mathematical differential diagnosis tool. Enter clinical data, symptoms, and lab values to get evidence-based diagnostic probabilities and clinical reasoning." />
        <link rel="canonical" href="https://phicor.ai/differential" />
        <meta property="og:title" content="Differential Diagnosis | PhiCor.ai" />
        <meta property="og:description" content="Mathematical differential diagnosis with clinical reasoning and evidence-based diagnostic probabilities." />
        <meta property="og:url" content="https://phicor.ai/differential" />
      </Helmet>
      <div className={`min-h-screen ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      <FeatureNav />
      
      <main className="container mx-auto px-4 py-6">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <span className="text-white font-serif text-3xl font-bold">Δ</span>
              </div>
              <div>
                <h1 className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Differential Diagnosis
                </h1>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Mathematical diagnostic reasoning engine
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={showDiseaseBrowser ? "default" : "outline"}
                size="sm"
                onClick={() => setShowDiseaseBrowser(!showDiseaseBrowser)}
                className={`hidden lg:flex items-center gap-1.5 ${showDiseaseBrowser ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`}
                data-testid="disease-browser-toggle"
              >
                <BookOpen className="w-4 h-4" />
                <span>Browse Diseases</span>
              </Button>
              <TutorialButton onClick={tutorial.open} />
            </div>
          </div>
        </div>
        
        {/* Tutorial Overlay */}
        <FeatureTutorial 
          feature="differential" 
          isOpen={tutorial.isOpen} 
          onClose={tutorial.close} 
        />
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Input Panel */}
          <div className="space-y-6">
            {/* Tabs */}
            <div className={`rounded-2xl border ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'} overflow-hidden`}>
              <div className="flex border-b border-gray-800" data-tutorial="input-tabs">
                {[
                  { id: 'parameters', icon: FlaskConical, label: 'Parameters' },
                  { id: 'clinical', icon: FileText, label: 'Clinical Text' },
                  { id: 'patient', icon: User, label: 'Test Patient' },
                  { id: 'calculators', icon: Calculator, label: 'Calculators' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium transition-all ${
                      activeTab === tab.id
                        ? `${isDark ? 'bg-emerald-500/20 text-emerald-400 border-b-2 border-emerald-500' : 'bg-emerald-50 text-emerald-600 border-b-2 border-emerald-500'}`
                        : `${isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800/50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
              
              <div className="p-4">
                {/* Parameters Tab */}
                {activeTab === 'parameters' && (
                  <div className="space-y-4">
                    {/* Organ Selection */}
                    <div>
                      <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Select Organ System
                      </label>
                      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-2">
                        {organs.slice(0, 12).map(organ => (
                          <button
                            key={organ.id}
                            onClick={() => {
                              setSelectedOrgan(organ.id);
                              setSelectedParameter('');
                            }}
                            className={`p-2 rounded-xl text-center transition-all ${
                              selectedOrgan === organ.id
                                ? 'bg-emerald-500 text-white shadow-lg'
                                : `${isDark ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`
                            }`}
                          >
                            <div className="text-xl mb-1">{organ.icon}</div>
                            <div className="text-xs truncate">{organ.name}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Parameter Selection */}
                    {selectedOrgan && organParameters.length > 0 && (
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Select Parameter
                        </label>
                        <select
                          value={selectedParameter}
                          onChange={(e) => setSelectedParameter(e.target.value)}
                          className={`w-full mt-2 p-3 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                        >
                          <option value="">Choose a parameter...</option>
                          {organParameters.map(param => (
                            <option key={param.id} value={param.id}>
                              {param.name} ({param.range[0]}-{param.range[1]} {param.unit})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {/* Value Slider */}
                    {selectedParameter && currentNormalRange && (
                      <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
                        <div className="flex items-center justify-between mb-3">
                          <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {currentNormalRange.name}
                          </span>
                          <Badge className={getStatusColor(getValueStatus(parameterValue, currentNormalRange))}>
                            {getValueStatus(parameterValue, currentNormalRange).replace('-', ' ')}
                          </Badge>
                        </div>
                        
                        {/* Normal Range Display */}
                        <div className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Normal: {currentNormalRange.min} - {currentNormalRange.max} {currentNormalRange.unit}
                        </div>
                        
                        {/* Slider */}
                        <div className="mb-4">
                          <Slider
                            value={sliderValue}
                            onValueChange={(val) => {
                              setSliderValue(val);
                              setParameterValue(val[0]);
                            }}
                            min={currentNormalRange.criticalLow}
                            max={currentNormalRange.criticalHigh}
                            step={(currentNormalRange.max - currentNormalRange.min) / 100}
                            className="w-full"
                          />
                        </div>
                        
                        {/* Value Input */}
                        <div className="flex items-center gap-3">
                          <Input
                            type="number"
                            value={parameterValue}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              setParameterValue(val);
                              setSliderValue([val]);
                            }}
                            className={`w-24 ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}
                          />
                          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {currentNormalRange.unit}
                          </span>
                          <Button
                            onClick={addFinding}
                            className="ml-auto bg-emerald-500 hover:bg-emerald-600 text-white"
                          >
                            <Plus className="w-4 h-4 mr-1" /> Add Finding
                          </Button>
                        </div>
                        
                        {/* Quick buttons */}
                        <div className="flex gap-2 mt-3">
                          {[
                            { label: 'Critical Low', value: currentNormalRange.criticalLow },
                            { label: 'Low', value: currentNormalRange.min * 0.8 },
                            { label: 'Normal', value: (currentNormalRange.min + currentNormalRange.max) / 2 },
                            { label: 'High', value: currentNormalRange.max * 1.2 },
                            { label: 'Critical High', value: currentNormalRange.criticalHigh },
                          ].map(btn => (
                            <button
                              key={btn.label}
                              onClick={() => {
                                setParameterValue(btn.value);
                                setSliderValue([btn.value]);
                              }}
                              className={`px-2 py-1 text-xs rounded-lg transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                            >
                              {btn.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Clinical Text Tab */}
                {activeTab === 'clinical' && (
                  <div className="space-y-4">
                    {/* AI interpretation toggle and region indicator */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={usePhiCor}
                            onChange={(e) => setUsePhiCor(e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
                          />
                          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            <Brain className="w-4 h-4 inline mr-1 text-emerald-500" />
                            PhiCor Analysis
                          </span>
                        </label>
                        {usePhiCor && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
                            Physiology-first multifactorial reasoning
                          </span>
                        )}
                      </div>
                      <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Units: {isSI ? 'SI (Metric)' : 'Conventional (US)'}
                      </span>
                    </div>
                    
                    <textarea
                      value={clinicalText}
                      onChange={(e) => setClinicalText(e.target.value)}
                      placeholder={`Paste clinical notes, lab results, or patient history here...

Example (any format works with PhiCor):
65 y/o M c/o CP and SOB x2 days
Labs: WBC 12.5k, Cr 1.8${isSI ? ' (159 μmol/L)' : ''}, Trop elevated, BNP 1200
PMH: HTN, T2DM, CKD stage 3
Echo: EF 35%, dilated LV, MR 2+, elevated PASP`}
                      className={`w-full h-48 p-4 rounded-xl border resize-none ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'}`}
                    />
                    <div className="flex gap-3">
                      <Button
                        onClick={handleAnalyzeText}
                        className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-6"
                        disabled={!clinicalText.trim() || isAnalyzing || isSynthesizing}
                      >
                        {isAnalyzing || isSynthesizing ? (
                          <>
                            <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            {isAnalyzing ? 'Analyzing...' : 'Synthesizing...'}
                          </>
                        ) : (
                          <>
                            <span className="font-serif font-bold mr-2">Φ</span>
                            Run Complete Analysis
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        className={isDark ? 'border-gray-700' : ''}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Document
                      </Button>
                    </div>
                    
                    {/* PhiCor Dominant Syndromes Display */}
                    {phicorResults && (phicorResults.dominant_syndromes?.length > 0 || phicorResults.syndromes?.length > 0) && (
                      <div className={`mt-4 p-4 rounded-xl border ${isDark ? 'bg-purple-900/20 border-purple-800' : 'bg-purple-50 border-purple-200'}`}>
                        <h4 className={`text-sm font-semibold flex items-center gap-2 mb-2 ${isDark ? 'text-purple-300' : 'text-purple-800'}`}>
                          <Target className="w-4 h-4" />
                          Dominant Clinical Syndrome(s)
                        </h4>
                        <div className="space-y-3">
                          {(phicorResults.dominant_syndromes || phicorResults.syndromes || []).map((syndrome, idx) => (
                            <div key={idx} className={`p-3 rounded-lg ${isDark ? 'bg-purple-900/30' : 'bg-purple-100/50'}`}>
                              <p className={`text-sm font-medium ${isDark ? 'text-purple-200' : 'text-purple-800'}`}>
                                {typeof syndrome === 'object' ? syndrome.syndrome : syndrome}
                              </p>
                              {syndrome.key_features?.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {syndrome.key_features.map((feature, i) => (
                                    <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-purple-800 text-purple-200' : 'bg-purple-200 text-purple-800'}`}>
                                      {feature}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {syndrome.pathophysiologic_rationale && (
                                <p className={`text-xs mt-2 italic ${isDark ? 'text-purple-300' : 'text-purple-600'}`}>
                                  {syndrome.pathophysiologic_rationale}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Clinical Findings Dashboard - ENHANCED */}
                    {phicorResults && phicorResults.findings?.length > 0 && (
                      <div className={`mt-4 p-4 rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                        <h4 className={`text-sm font-semibold flex items-center gap-2 mb-4 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          <Activity className="w-4 h-4" />
                          Clinical Findings Dashboard
                          <Badge className={`ml-2 ${isDark ? 'bg-emerald-900/50 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
                            {phicorResults.findings.length} parameters
                          </Badge>
                        </h4>
                        
                        {/* Big Value Cards Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                          {phicorResults.findings.slice(0, 12).map((finding, idx) => {
                            const isHigh = finding.direction === 'increase' || finding.status === 'elevated' || finding.status === 'high';
                            const isLow = finding.direction === 'decrease' || finding.status === 'low' || finding.status === 'decreased';
                            const isAbnormal = isHigh || isLow;
                            
                            return (
                              <div 
                                key={idx} 
                                className={`p-4 rounded-xl border-2 transition-all hover:scale-[1.02] cursor-pointer ${
                                  isHigh 
                                    ? isDark ? 'bg-red-900/30 border-red-500/50' : 'bg-red-50 border-red-300'
                                    : isLow
                                      ? isDark ? 'bg-blue-900/30 border-blue-500/50' : 'bg-blue-50 border-blue-300'
                                      : isDark ? 'bg-gray-700/30 border-gray-600' : 'bg-white border-gray-200'
                                }`}
                                onClick={() => {
                                  setDrillDownModal({
                                    isOpen: true,
                                    parameterName: finding.parameter_id || finding.name,
                                    parameterType: 'finding',
                                    expectedValue: finding,
                                    currentDirection: finding.direction
                                  });
                                }}
                              >
                                {/* Parameter Name */}
                                <div className="flex items-center justify-between mb-2">
                                  <span className={`text-xs font-medium uppercase tracking-wide truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {finding.name?.replace(/_/g, ' ')}
                                  </span>
                                  {isHigh && <TrendingUp className="w-4 h-4 text-red-500 flex-shrink-0" />}
                                  {isLow && <TrendingDown className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                                  {!isAbnormal && <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />}
                                </div>
                                
                                {/* Big Value Display */}
                                <div className={`text-2xl md:text-3xl font-bold mb-1 ${
                                  isHigh ? 'text-red-500'
                                    : isLow ? 'text-blue-500'
                                      : isDark ? 'text-emerald-400' : 'text-emerald-600'
                                }`}>
                                  {finding.value}
                                  <span className={`text-sm font-normal ml-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {finding.unit}
                                  </span>
                                </div>
                                
                                {/* Normal Range */}
                                {finding.normal_range && (
                                  <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    Normal: {finding.normal_range}
                                  </div>
                                )}
                                
                                {/* Severity Badge */}
                                {isAbnormal && (
                                  <Badge className={`mt-2 text-[10px] ${
                                    isHigh 
                                      ? 'bg-red-500/20 text-red-400 border-red-500/30'
                                      : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                                  }`}>
                                    {isHigh ? '↑ ELEVATED' : '↓ LOW'}
                                  </Badge>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Safety Considerations */}
                    {phicorResults && phicorResults.safety_alerts?.length > 0 && (
                      <div className={`mt-3 p-4 rounded-xl border ${isDark ? 'bg-amber-900/20 border-amber-800' : 'bg-amber-50 border-amber-200'}`}>
                        <h4 className={`text-sm font-semibold flex items-center gap-2 mb-2 ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                          <AlertTriangle className="w-4 h-4" />
                          Safety Considerations
                        </h4>
                        <ul className="space-y-2">
                          {phicorResults.safety_alerts.map((alert, idx) => (
                            <li key={idx} className={`p-2 rounded-lg ${isDark ? 'bg-amber-900/30' : 'bg-amber-100/50'}`}>
                              {typeof alert === 'object' ? (
                                <div>
                                  <span className={`text-sm font-medium ${isDark ? 'text-amber-200' : 'text-amber-800'}`}>
                                    {alert.alert_type}: {alert.parameter}
                                  </span>
                                  <p className={`text-xs ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                                    {alert.message}
                                  </p>
                                  {alert.recommended_action && (
                                    <p className={`text-xs mt-1 italic ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                                      → {alert.recommended_action}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <span className={`text-sm ${isDark ? 'text-amber-200' : 'text-amber-700'}`}>
                                  • {alert}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {phicorResults && phicorResults.synthesis && (
                      <div className={`mt-3 p-4 rounded-xl border ${isDark ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                        <h4 className={`text-sm font-semibold flex items-center gap-2 mb-2 ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                          <Sparkles className="w-4 h-4" />
                          Diagnostic Synthesis
                        </h4>
                        <div className="space-y-3 text-sm">
                          <div className="flex gap-4 flex-wrap">
                            {phicorResults.synthesis.primary_diagnosis && (
                              <div className={`flex-1 min-w-[200px] p-3 rounded-lg ${isDark ? 'bg-emerald-900/30' : 'bg-emerald-100/50'}`}>
                                <p className={`text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Most Likely</p>
                                <p className={`font-semibold ${isDark ? 'text-emerald-200' : 'text-emerald-800'}`}>
                                  {phicorResults.synthesis.primary_diagnosis}
                                </p>
                              </div>
                            )}
                            {phicorResults.synthesis.key_alternative && (
                              <div className={`flex-1 min-w-[200px] p-3 rounded-lg ${isDark ? 'bg-blue-900/30' : 'bg-blue-100/50'}`}>
                                <p className={`text-xs ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Key Alternative</p>
                                <p className={`font-semibold ${isDark ? 'text-blue-200' : 'text-blue-800'}`}>
                                  {phicorResults.synthesis.key_alternative}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          {phicorResults.synthesis.clinical_decision_pathway && (
                            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                              <p className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Clinical Decision Pathway</p>
                              <p className={isDark ? 'text-gray-200' : 'text-gray-700'}>
                                {phicorResults.synthesis.clinical_decision_pathway}
                              </p>
                            </div>
                          )}
                          
                          {phicorResults.synthesis.discriminating_tests?.length > 0 && (
                            <div className="mt-2">
                              <strong className={isDark ? 'text-blue-200' : 'text-blue-700'}>Discriminating Tests:</strong>
                              <div className="mt-2 space-y-2">
                                {phicorResults.synthesis.discriminating_tests.map((test, idx) => (
                                  <div key={idx} className={`p-2 rounded-lg text-xs ${isDark ? 'bg-blue-900/30' : 'bg-blue-100/30'}`}>
                                    <span className={`font-medium ${isDark ? 'text-blue-200' : 'text-blue-800'}`}>
                                      {typeof test === 'object' ? test.test : test}
                                    </span>
                                    {test.expected_finding && (
                                      <span className={`block mt-1 ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>
                                        → Expected: {test.expected_finding}
                                      </span>
                                    )}
                                    {test.rationale && (
                                      <span className={`block mt-1 italic ${isDark ? 'text-blue-400' : 'text-blue-500'}`}>
                                        {test.rationale}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {phicorResults.synthesis.clinical_uncertainty && (
                            <p className={`text-xs italic mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              ⚠️ {phicorResults.synthesis.clinical_uncertainty}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Management Considerations */}
                    {phicorResults && phicorResults.management?.length > 0 && (
                      <div className={`mt-3 p-4 rounded-xl border ${isDark ? 'bg-emerald-900/20 border-emerald-800' : 'bg-emerald-50 border-emerald-200'}`}>
                        <h4 className={`text-sm font-semibold flex items-center gap-2 mb-3 ${isDark ? 'text-emerald-300' : 'text-emerald-800'}`}>
                          <Stethoscope className="w-4 h-4" />
                          Management Considerations
                        </h4>
                        <div className="space-y-4">
                          {phicorResults.management.map((mgmt, idx) => (
                            <div key={idx} className={`p-3 rounded-lg ${isDark ? 'bg-emerald-900/30' : 'bg-emerald-100/50'}`}>
                              <p className={`font-medium text-sm mb-2 ${isDark ? 'text-emerald-200' : 'text-emerald-800'}`}>
                                {mgmt.diagnosis}
                              </p>
                              {mgmt.approaches?.length > 0 && (
                                <ul className="space-y-1 mb-2">
                                  {mgmt.approaches.map((approach, i) => (
                                    <li key={i} className={`text-xs ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                                      • {approach}
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {mgmt.monitoring?.length > 0 && (
                                <p className={`text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                  <strong>Monitor:</strong> {mgmt.monitoring.join(', ')}
                                </p>
                              )}
                              {mgmt.cautions?.length > 0 && (
                                <p className={`text-xs mt-1 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                                  <strong>⚠️ Caution:</strong> {mgmt.cautions.join(', ')}
                                </p>
                              )}
                              {mgmt.source && (
                                <p className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                  Source: {mgmt.source}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                        <p className={`text-[10px] mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'} italic`}>
                          Management information is for clinical context and education. All treatment decisions require clinician judgment.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Test Patient Tab */}
                {activeTab === 'patient' && (
                  <TestPatientPanel 
                    isDark={isDark}
                    onLoadCase={(caseData) => {
                      setClinicalText(caseData.clinicalText);
                      setActiveTab('clinical');
                      // Auto-trigger analysis after a short delay
                      setTimeout(() => {
                        handleAnalyzeText();
                      }, 500);
                    }}
                  />
                )}
                
                {/* Calculators Tab */}
                {activeTab === 'calculators' && (
                  <div className="h-[650px] overflow-hidden">
                    <ClinicalCalculatorPanel isDark={isDark} />
                  </div>
                )}
              </div>
            </div>
            
            {/* Current Findings - ENHANCED DASHBOARD STYLE */}
            {abnormalFindings.length > 0 && (
              <div className={`rounded-2xl border ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'} p-4`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Activity className="w-4 h-4 text-emerald-500" />
                    Clinical Findings Dashboard
                    <Badge className="ml-2 bg-emerald-500/20 text-emerald-400">{abnormalFindings.length} parameters</Badge>
                  </h3>
                </div>
                
                {/* Big Value Cards Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {abnormalFindings.slice(0, 12).map(finding => {
                    const isHigh = finding.direction === 'increase';
                    const isLow = finding.direction === 'decrease';
                    const isSymptom = finding.type === 'symptom' || finding.direction === 'present';
                    const severity = finding.severity || 'moderate';
                    const specificity = finding.specificity || 'supportive';
                    
                    // Determine symptom severity styling
                    const getSeverityStyle = () => {
                      if (severity === 'severe') return isDark ? 'bg-orange-900/30 border-orange-500/50' : 'bg-orange-50 border-orange-300';
                      if (severity === 'mild') return isDark ? 'bg-gray-800/50 border-gray-600/50' : 'bg-gray-100 border-gray-300';
                      return isDark ? 'bg-purple-900/30 border-purple-500/50' : 'bg-purple-50 border-purple-300';
                    };
                    
                    return (
                      <div 
                        key={finding.id}
                        className={`relative p-4 rounded-xl border-2 transition-all hover:scale-[1.02] ${
                          isHigh 
                            ? isDark ? 'bg-red-900/30 border-red-500/50' : 'bg-red-50 border-red-300'
                            : isLow
                              ? isDark ? 'bg-blue-900/30 border-blue-500/50' : 'bg-blue-50 border-blue-300'
                              : isSymptom
                                ? getSeverityStyle()
                                : isDark ? 'bg-emerald-900/30 border-emerald-500/50' : 'bg-emerald-50 border-emerald-300'
                        }`}
                      >
                        {/* Delete button */}
                        <button 
                          onClick={() => removeFinding(finding.id)} 
                          className={`absolute top-2 right-2 p-1 rounded-full opacity-50 hover:opacity-100 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                        
                        {/* Parameter Name */}
                        <div className="flex items-center gap-2 mb-2">
                          {isHigh && <TrendingUp className="w-4 h-4 text-red-500" />}
                          {isLow && <TrendingDown className="w-4 h-4 text-blue-500" />}
                          {!isHigh && !isLow && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                          <span className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {finding.parameterName?.replace(/_/g, ' ')}
                          </span>
                        </div>
                        
                        {/* Big Value */}
                        <div className={`text-2xl font-bold ${
                          isHigh ? 'text-red-500' : isLow ? 'text-blue-500' : isDark ? 'text-emerald-400' : 'text-emerald-600'
                        }`}>
                          {finding.value || '—'}
                          {finding.unit && (
                            <span className={`text-sm font-normal ml-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              {finding.unit}
                            </span>
                          )}
                        </div>
                        
                        {/* Normal Range */}
                        {finding.normalRange && (
                          <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Normal: {finding.normalRange}
                          </div>
                        )}
                        
                        {/* Status Badge - Show severity for symptoms, direction for labs/vitals */}
                        <Badge className={`mt-2 text-[10px] ${
                          isHigh ? 'bg-red-500/20 text-red-400' 
                            : isLow ? 'bg-blue-500/20 text-blue-400' 
                            : isSymptom 
                              ? severity === 'severe' ? 'bg-orange-500/20 text-orange-400'
                                : severity === 'mild' ? 'bg-gray-500/20 text-gray-400'
                                : 'bg-purple-500/20 text-purple-400'
                              : 'bg-emerald-500/20 text-emerald-400'
                        }`}>
                          {isHigh ? '↑ ELEVATED' 
                            : isLow ? '↓ LOW' 
                            : isSymptom 
                              ? `⚠ ${severity.toUpperCase()}${specificity === 'nonspecific' ? ' (nonspecific)' : ''}`
                              : '✓ PRESENT'}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
                
                {abnormalFindings.length > 12 && (
                  <p className={`mt-3 text-xs text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    + {abnormalFindings.length - 12} more findings
                  </p>
                )}
              </div>
            )}
          </div>
          
          {/* Right: Results Panel */}
          <div className={`rounded-2xl border ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'} overflow-hidden`}>
            <div className={`p-4 border-b ${isDark ? 'border-gray-800 bg-gradient-to-r from-emerald-900/30 to-teal-900/30' : 'border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50'}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Stethoscope className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Differential Diagnosis
                  </h3>
                  {/* Patient Context Indicator */}
                  {!activePatient?.isBaseline && (
                    <Badge className="bg-cyan-500/20 text-cyan-400 text-[10px]">
                      <User className="w-3 h-3 mr-1" />
                      {activePatient.name || 'Patient'}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {differentialResults.length > 0 && (
                    <Badge className="bg-emerald-500/20 text-emerald-400">
                      {differentialResults.length} conditions
                    </Badge>
                  )}
                  {analysisTimeTaken && !isAnalyzing && differentialResults.length > 0 && (
                    <Badge className="bg-blue-500/20 text-blue-400 flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      {analysisTimeTaken}s
                    </Badge>
                  )}
                  {/* Patient Modifiers Active */}
                  {!activePatient?.isBaseline && modifiers && Object.keys(modifiers).length > 0 && (
                    <Badge className="bg-orange-500/20 text-orange-400 text-[10px]">
                      Modifiers Active
                    </Badge>
                  )}
                  {/* Show synthesizing indicator */}
                  {isSynthesizing && (
                    <Badge className="bg-purple-500/20 text-purple-400 flex items-center gap-1">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-400 mr-1" />
                      Synthesizing...
                    </Badge>
                  )}
                  {/* Save & View Cases Buttons */}
                  {differentialResults.length > 0 && !isAnalyzing && (
                    <>
                      <Button
                        onClick={saveCase}
                        disabled={isSaving}
                        size="sm"
                        className="h-7 px-2 bg-cyan-600 hover:bg-cyan-700 text-white"
                      >
                        {isSaving ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1" />
                        ) : (
                          <Save className="w-3 h-3 mr-1" />
                        )}
                        Save
                      </Button>
                      <Button
                        onClick={() => navigate('/cases')}
                        size="sm"
                        variant="outline"
                        className="h-7 px-2"
                      >
                        <FolderOpen className="w-3 h-3 mr-1" />
                        Cases
                      </Button>
                      {/* PDF Export Button */}
                      <PDFExportButton 
                        type="diagnosis"
                        data={{
                          differentials: differentialResults,
                          recommended_workup: phicorResults?.synthesis?.discriminating_tests || [],
                          synthesis: phicorResults?.synthesis
                        }}
                        patientInput={clinicalText}
                        patientProfile={{
                          age: abnormalFindings.find(f => f.name?.toLowerCase().includes('age'))?.value,
                          gender: abnormalFindings.find(f => f.name?.toLowerCase().includes('gender'))?.value
                        }}
                        isDark={isDark}
                        variant="outline"
                        size="sm"
                        className="h-7"
                      />
                      
                      {/* View Toggle: List vs Mind Map vs Body Map */}
                      <div className="flex items-center border rounded-lg overflow-hidden ml-1">
                        <Button
                          onClick={() => { setShowNeuralNetwork(false); setShowBodyMap(false); }}
                          size="sm"
                          variant={!showNeuralNetwork && !showBodyMap ? "default" : "ghost"}
                          className={`h-7 px-2 rounded-none ${!showNeuralNetwork && !showBodyMap ? 'bg-emerald-600' : ''}`}
                          title="List View"
                          data-testid="view-list-btn"
                        >
                          <LayoutList className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          onClick={() => { setShowNeuralNetwork(true); setShowBodyMap(false); }}
                          size="sm"
                          variant={showNeuralNetwork ? "default" : "ghost"}
                          className={`h-7 px-2 rounded-none ${showNeuralNetwork ? 'bg-purple-600' : ''}`}
                          title="Mind Map View"
                          data-testid="view-network-btn"
                        >
                          <Network className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          onClick={() => { setShowNeuralNetwork(false); setShowBodyMap(true); }}
                          size="sm"
                          variant={showBodyMap ? "default" : "ghost"}
                          className={`h-7 px-2 rounded-none ${showBodyMap ? 'bg-cyan-600' : ''}`}
                          title="Body Map View"
                          data-testid="view-body-map-btn"
                        >
                          <Layers className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <ScrollArea className="h-[600px]">
              <div className="p-4">
                {/* PhiCor Synthesis Section - FIRST */}
                {(isSynthesizing || clinicalSynthesis) && (
                  <div className={`mb-6 rounded-xl border-2 ${isDark ? 'bg-gradient-to-br from-purple-900/30 to-indigo-900/30 border-purple-500/50' : 'bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-300'} overflow-hidden`}>
                    <div 
                      className={`p-4 cursor-pointer flex items-center justify-between ${isDark ? 'bg-purple-900/20' : 'bg-purple-100/50'}`}
                      onClick={() => setSynthesisExpanded(!synthesisExpanded)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center shadow-lg">
                          <span className="text-white font-serif text-xl font-bold">Φ</span>
                        </div>
                        <div>
                          <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            PhiCor Synthesis
                          </h3>
                          <p className={`text-xs ${isDark ? 'text-purple-300' : 'text-purple-600'}`}>
                            Holistic analysis connecting all findings
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isSynthesizing && (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500" />
                        )}
                        {synthesisExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                    
                    {synthesisExpanded && clinicalSynthesis && (
                      <div className="p-4 space-y-4">
                        {/* Clinical Synthesis Summary */}
                        {clinicalSynthesis.clinical_synthesis && (
                          <div className={`p-4 rounded-lg ${isDark ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
                            <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>
                              <Target className="w-4 h-4" /> Clinical Pattern Recognition
                            </h4>
                            <p className={`text-sm mb-3 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                              {clinicalSynthesis.clinical_synthesis.summary}
                            </p>
                            {clinicalSynthesis.clinical_synthesis.key_pattern && (
                              <div className={`text-xs p-2 rounded ${isDark ? 'bg-purple-800/50' : 'bg-purple-200'}`}>
                                <strong>Key Pattern:</strong> {clinicalSynthesis.clinical_synthesis.key_pattern}
                              </div>
                            )}
                            {clinicalSynthesis.clinical_synthesis.clinical_pearl && (
                              <div className={`mt-2 text-xs p-2 rounded ${isDark ? 'bg-indigo-800/50' : 'bg-indigo-200'}`}>
                                <strong>💡 Clinical Pearl:</strong> {clinicalSynthesis.clinical_synthesis.clinical_pearl}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Primary Diagnosis from AI */}
                        {clinicalSynthesis.primary_diagnosis && (
                          <div className={`p-4 rounded-lg border ${isDark ? 'bg-emerald-900/20 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200'}`}>
                            <div className="flex items-center justify-between mb-3">
                              <h4 className={`text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                                <Target className="w-4 h-4" /> Primary Diagnosis
                              </h4>
                              <Badge className="bg-emerald-500 text-white">
                                {clinicalSynthesis.primary_diagnosis.probability}% likely
                              </Badge>
                            </div>
                            <p className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {clinicalSynthesis.primary_diagnosis.name}
                            </p>
                            <p className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {clinicalSynthesis.primary_diagnosis.icd10}
                            </p>
                            <div className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                              {clinicalSynthesis.primary_diagnosis.reasoning}
                            </div>
                            
                            {/* Key Supporting Findings */}
                            {clinicalSynthesis.primary_diagnosis.key_supporting_findings?.length > 0 && (
                              <div className="mt-3">
                                <p className={`text-xs font-semibold mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Supporting Evidence:
                                </p>
                                <div className="space-y-1">
                                  {clinicalSynthesis.primary_diagnosis.key_supporting_findings.map((f, i) => (
                                    <div key={i} className={`text-xs flex items-start gap-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                      <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                                      <span><strong>{f.finding}:</strong> {f.significance}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Next Steps */}
                            {clinicalSynthesis.primary_diagnosis.next_steps?.length > 0 && (
                              <div className="mt-3">
                                <p className={`text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Recommended Next Steps:
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {clinicalSynthesis.primary_diagnosis.next_steps.map((step, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">
                                      {step}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Guideline Source */}
                            {(clinicalSynthesis.primary_diagnosis.guideline_source || clinicalSynthesis.primary_diagnosis.database_match) && (
                              <div className={`mt-3 text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                {clinicalSynthesis.primary_diagnosis.database_match && (
                                  <span className="mr-3">📚 PhiCor DB: {clinicalSynthesis.primary_diagnosis.database_match}</span>
                                )}
                                {clinicalSynthesis.primary_diagnosis.guideline_source && (
                                  <span>📋 {clinicalSynthesis.primary_diagnosis.guideline_source}</span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Recommended Treatment from Drug Database */}
                        {clinicalSynthesis.recommended_treatment?.pharmacological?.length > 0 && (
                          <div className={`p-4 rounded-lg border ${isDark ? 'bg-cyan-900/20 border-cyan-500/30' : 'bg-cyan-50 border-cyan-200'}`}>
                            <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`}>
                              <Pill className="w-4 h-4" /> Treatment Recommendations
                            </h4>
                            <div className="space-y-2">
                              {clinicalSynthesis.recommended_treatment.pharmacological.map((drug, i) => (
                                <div key={i} className={`p-2 rounded ${isDark ? 'bg-cyan-900/30' : 'bg-cyan-100'}`}>
                                  <div className="flex items-center justify-between">
                                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                      {drug.drug}
                                    </span>
                                    {drug.evidence_level && (
                                      <Badge className="bg-cyan-500/20 text-cyan-400 text-xs">
                                        Level {drug.evidence_level}
                                      </Badge>
                                    )}
                                  </div>
                                  {drug.dose && (
                                    <p className={`text-xs ${isDark ? 'text-cyan-300' : 'text-cyan-600'}`}>
                                      Dose: {drug.dose}
                                    </p>
                                  )}
                                  {drug.rationale && (
                                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                      {drug.rationale}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                            {clinicalSynthesis.recommended_treatment.guideline_source && (
                              <p className={`text-xs mt-2 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
                                Source: {clinicalSynthesis.recommended_treatment.guideline_source}
                              </p>
                            )}
                          </div>
                        )}
                        
                        {/* Knowledge Base References */}
                        {clinicalSynthesis.knowledge_base && (
                          <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800/30' : 'bg-gray-50'} flex items-center justify-between`}>
                            <div className="flex items-center gap-4 text-xs">
                              <span className={`${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                                📊 {clinicalSynthesis.knowledge_base.diseases_referenced} diseases referenced
                              </span>
                              <span className={`${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
                                💊 {clinicalSynthesis.knowledge_base.drugs_referenced} drugs matched
                              </span>
                            </div>
                            <Badge className={`text-xs ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
                              PhiCor Integrated
                            </Badge>
                          </div>
                        )}
                        
                        {/* Clinical Reasoning Steps */}
                        {clinicalSynthesis.clinical_reasoning_steps?.length > 0 && (
                          <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
                            <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              <Brain className="w-4 h-4" /> Diagnostic Reasoning Process
                            </h4>
                            <div className="space-y-3">
                              {clinicalSynthesis.clinical_reasoning_steps.map((step, i) => (
                                <div key={i} className="flex gap-3">
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-300 text-gray-700'}`}>
                                    {step.step}
                                  </div>
                                  <div>
                                    <p className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                                      {step.thought}
                                    </p>
                                    {step.hypothesis && (
                                      <p className={`text-xs mt-1 ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                                        → {step.hypothesis}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Mathematical Analysis */}
                        {clinicalSynthesis.mathematical_analysis && (
                          <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                            <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                              <Gauge className="w-4 h-4" /> Bayesian Probability Analysis
                            </h4>
                            {clinicalSynthesis.mathematical_analysis.pre_test_probability && (
                              <p className={`text-xs mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                <strong>Pre-test probability:</strong> {clinicalSynthesis.mathematical_analysis.pre_test_probability}
                              </p>
                            )}
                            {clinicalSynthesis.mathematical_analysis.combined_probability_estimate && (
                              <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                <strong>Combined analysis:</strong> {clinicalSynthesis.mathematical_analysis.combined_probability_estimate}
                              </p>
                            )}
                          </div>
                        )}
                        
                        {/* Urgency Assessment */}
                        {clinicalSynthesis.urgency_assessment && (
                          <div className={`p-3 rounded-lg flex items-center gap-3 ${
                            clinicalSynthesis.urgency_assessment.level === 'emergent' 
                              ? (isDark ? 'bg-red-900/30 border border-red-500/50' : 'bg-red-50 border border-red-200')
                              : clinicalSynthesis.urgency_assessment.level === 'urgent'
                              ? (isDark ? 'bg-orange-900/30 border border-orange-500/50' : 'bg-orange-50 border border-orange-200')
                              : (isDark ? 'bg-gray-800/50' : 'bg-gray-100')
                          }`}>
                            <Shield className={`w-5 h-5 ${
                              clinicalSynthesis.urgency_assessment.level === 'emergent' ? 'text-red-500' :
                              clinicalSynthesis.urgency_assessment.level === 'urgent' ? 'text-orange-500' : 'text-gray-500'
                            }`} />
                            <div>
                              <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                PhiCor Alert: {clinicalSynthesis.urgency_assessment.level?.toUpperCase()}
                              </p>
                              {clinicalSynthesis.urgency_assessment.can_miss_diagnosis && (
                                <p className={`text-xs ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                                  ⚠️ Do not miss: {clinicalSynthesis.urgency_assessment.can_miss_diagnosis}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Divider */}
                {clinicalSynthesis && differentialResults.length > 0 && (
                  <div className={`mb-4 py-2 flex items-center gap-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    <div className="flex-1 h-px bg-current opacity-30" />
                    <span className="text-xs uppercase tracking-wider">Individual Parameter Analysis</span>
                    <div className="flex-1 h-px bg-current opacity-30" />
                  </div>
                )}
                
                {isAnalyzing ? (
                  <div className={`flex flex-col items-center justify-center py-12 px-4 ${isDark ? 'bg-gray-800/30' : 'bg-gray-50'} rounded-xl`}>
                    {/* Animated DNA/Medical Helix */}
                    <div className="relative w-20 h-20 mb-6">
                      <div className="absolute inset-0 rounded-full border-4 border-emerald-500/30 animate-ping" />
                      <div className="absolute inset-2 rounded-full border-4 border-t-emerald-500 border-r-transparent border-b-emerald-500/50 border-l-transparent animate-spin" />
                      <div className="absolute inset-4 rounded-full border-4 border-t-transparent border-r-purple-500 border-b-transparent border-l-purple-500/50 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-2xl font-serif font-bold text-emerald-400">Φ</span>
                      </div>
                    </div>
                    
                    <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      PhiCor Analysis in Progress
                    </h3>
                    
                    {/* Dynamic Analysis Step */}
                    <div className={`text-sm mb-4 ${isDark ? 'text-emerald-400' : 'text-emerald-600'} animate-pulse`}>
                      {analysisStep || 'Initializing analysis...'}
                    </div>
                    
                    {/* Live Timer */}
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full mb-4 ${isDark ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
                      <Activity className="w-4 h-4 animate-pulse" />
                      <span className="font-mono font-semibold">{elapsedTime}s</span>
                    </div>
                    
                    {/* Currently Analyzing Text - Highlighted */}
                    {highlightedText && (
                      <div className={`w-full max-w-md p-4 rounded-lg mb-4 border-2 border-dashed ${isDark ? 'bg-purple-900/20 border-purple-500/50' : 'bg-purple-50 border-purple-300'}`}>
                        <p className={`text-xs mb-2 ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                          Currently analyzing:
                        </p>
                        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'} animate-pulse`}>
                          &ldquo;{highlightedText}&rdquo;
                        </p>
                      </div>
                    )}
                    
                    {/* Progress Steps */}
                    <div className={`w-full max-w-sm text-xs space-y-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      <div className="flex items-center gap-2">
                        <CheckCircle className={`w-3 h-3 ${elapsedTime >= 0 ? 'text-emerald-500' : 'text-gray-400'}`} />
                        <span className={elapsedTime >= 0 ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : ''}>Parsing clinical text...</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full border-2 transition-all ${elapsedTime > 5 ? 'bg-emerald-500 border-emerald-500' : 'border-gray-400 animate-pulse'}`} />
                        <span className={elapsedTime > 5 ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : ''}>Extracting vital signs & lab values...</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full border-2 transition-all ${elapsedTime > 15 ? 'bg-emerald-500 border-emerald-500' : 'border-gray-400'}`} />
                        <span className={elapsedTime > 15 ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : ''}>Running differential diagnosis...</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full border-2 transition-all ${elapsedTime > 30 ? 'bg-emerald-500 border-emerald-500' : 'border-gray-400'}`} />
                        <span className={elapsedTime > 30 ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : ''}>Matching disease profiles & models...</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full border-2 transition-all ${elapsedTime > 45 ? 'bg-emerald-500 border-emerald-500' : 'border-gray-400'}`} />
                        <span className={elapsedTime > 45 ? (isDark ? 'text-purple-400' : 'text-purple-600') : ''}>Generating clinical synthesis...</span>
                      </div>
                    </div>
                  </div>
                ) : differentialResults.length > 0 ? (
                  <>
                    {/* Time Taken Badge */}
                    {analysisTimeTaken && (
                      <div className={`mb-4 flex items-center justify-center gap-2 text-sm ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        <Zap className="w-4 h-4" />
                        <span>Analysis completed in <strong>{analysisTimeTaken}s</strong></span>
                      </div>
                    )}
                    
                    {/* Toggle between List View, Neural Network View, and Body Map View */}
                    {showNeuralNetwork ? (
                      <div className="space-y-4">
                        <NeuralNetworkVisualization
                          nodes={neuralNetworkData.nodes}
                          connections={neuralNetworkData.connections}
                          centralNode="patient"
                          mode="radial"
                          title="Diagnostic Reasoning Map"
                          height={520}
                          isDark={isDark}
                          onNodeClick={(node) => {
                            if (node.type === 'diagnosis') {
                              // Find and expand the diagnosis in the list
                              const diagIndex = differentialResults.findIndex(d => 
                                (d.diseaseName || d.name) === node.label
                              );
                              if (diagIndex !== -1) {
                                setShowNeuralNetwork(false);
                                // Could scroll to or expand this diagnosis
                              }
                            }
                          }}
                        />
                        <div className={`text-center text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          <Info className="w-3 h-3 inline mr-1" />
                          Click nodes to see details • Toggle between views using the buttons above
                        </div>
                      </div>
                    ) : showBodyMap ? (
                      <div className="space-y-4">
                        <EnhancedBodyMap
                          effects={bodyMapData.effects}
                          connections={bodyMapData.connections}
                          sourceLabel={differentialResults[0]?.diseaseName || 'Diagnosis'}
                          height={520}
                          isDark={isDark}
                          onOrganClick={(organ) => {
                            console.log('Organ clicked:', organ.name);
                            // Could filter findings by organ
                          }}
                        />
                        <div className={`text-center text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          <Info className="w-3 h-3 inline mr-1" />
                          Click organs to see detailed parameter effects • Green = positive, Red = concern
                        </div>
                      </div>
                    ) : (
                      <DifferentialDashboard
                        differentials={differentialResults}
                        patientFindings={abnormalFindings}
                        symptoms={abnormalFindings.filter(f => f.type === 'symptom')}
                        onParameterClick={(paramName, type, expected) => {
                          console.log('Parameter clicked:', paramName, type, expected);
                          // Open Parameter Drill-Down Modal
                          setDrillDownModal({
                            isOpen: true,
                            parameterName: paramName,
                            parameterType: type,
                            expectedValue: expected,
                            currentDirection: expected?.direction || (type === 'symptom' ? 'present' : null)
                          });
                        }}
                      />
                    )}
                  </>
                ) : (
                  <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    <Brain className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium mb-2">No findings yet</p>
                    <p className="text-sm">Add abnormal parameters or paste clinical text to generate differential diagnosis</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </main>
      
      {/* Parameter Drill-Down Modal */}
      <ParameterDrillDownModal
        isOpen={drillDownModal.isOpen}
        onClose={() => setDrillDownModal(prev => ({ ...prev, isOpen: false }))}
        parameterName={drillDownModal.parameterName}
        parameterType={drillDownModal.parameterType}
        expectedValue={drillDownModal.expectedValue}
        currentDirection={drillDownModal.currentDirection}
      />
      
      {/* Disease Browser Sidebar */}
      <DiseaseBrowserPanel
        isOpen={showDiseaseBrowser}
        onClose={() => setShowDiseaseBrowser(false)}
        onSelectDisease={(disease) => {
          // Add disease to clinical text or filter findings
          const currentText = clinicalText || '';
          const diseaseInfo = `${disease.name} (ICD-10: ${disease.icd10_code || 'N/A'})`;
          setClinicalText(currentText ? `${currentText}\n\nCondition: ${diseaseInfo}` : `Condition: ${diseaseInfo}`);
          setShowDiseaseBrowser(false);
          setActiveTab('clinical');
          toast.success(`Added ${disease.name} to clinical text`);
        }}
      />
    </div>
    </>
  );
};

export default DifferentialPage;
